import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import Sidebar from "./Sidebar";
import MessagePane from "./MessagePane";
import QuickSwitcher from "./QuickSwitcher";
import { rememberOpenedDm } from "./openedDms";
import { CallProvider } from "./CallManager";
import { Selection } from "./useChatData";
import { useNotifications } from "./useNotifications";
import { useLaterEngine } from "./useLaterEngine";
import { useActivityLogger } from "./useActivityLogger";
import { useGroupSync } from "./useGroupSync";
import { useSessionRecovery } from "./useSessionRecovery";
import LaterPanel from "./LaterPanel";
import ActivityPanel from "./ActivityPanel";
import CallsPanel from "./CallsPanel";
import FilesPanel from "./FilesPanel";
import ProfilePanel from "./ProfilePanel";
import AvatarHoverCard from "./AvatarHoverCard";
import Avatar from "./Avatar";
import { clockLabel } from "./format";
import { markActivitySeen, subscribeActivity, unseenActivityCount } from "./activity";
import { SunIcon, MoonIcon, SparkleIcon } from "./Icons";
import ReleaseNotes, { useHasUnseenRelease } from "./ReleaseNotes";
import NameSetupModal from "./NameSetupModal";
import { readNameLocked, splitName } from "./nameSetup";
import NotificationBell from "./NotificationBell";
import BroadcastToast from "./BroadcastToast";
import AdminPanel from "./AdminPanel";
import { useBroadcastNotifications, checkAdminStatus } from "./broadcastNotify";
import type { AdminInfo } from "./broadcastNotify";
import { ShieldIcon } from "./Icons";
import * as sounds from "./sounds";
import "./dbm-workspace.css";

/* Bumped from "dbm-theme" when light became the default. The old key was
   written on every mount, so every existing user already had "dark"
   persisted from back when that was the default — reading it would have
   meant the new light default never reached anyone who'd used the app
   before. A new key ignores those stale values once; anything saved from
   here on is a real, deliberate choice. */
const THEME_KEY = "dbm-theme-v2";
const IS_MAC = /mac/i.test(navigator.platform || "");

/* Trailing U+FE0E (text variation selector) pins these to monochrome glyph
   rendering — without it, some platforms substitute a colour-emoji font
   for symbols like the phone/flag/stopwatch, which ignores our CSS
   `color` entirely and can render as an alarming red/full-colour icon. */
const RAIL_TABS = [
  { key: "home", icon: "▤︎", label: "Home" },
  { key: "activity", icon: "◉︎", label: "Activity" },
  { key: "calls", icon: "✆︎", label: "Calls" },
  { key: "files", icon: "❐︎", label: "Files" },
  { key: "later", icon: "⚑︎", label: "Later" },
] as const;

/** Origins allowed to receive our pop-out request — same list DBMGate trusts for the login handoff. */
const ALLOWED_PARENT_ORIGINS = (
  process.env.REACT_APP_ALLOWED_PARENT_ORIGINS ||
  "https://dbmpartnerportal.com.au,https://www.dbmpartnerportal.com.au"
)
  .split(",")
  .map((o) => o.trim());

/**
 * dbm. chat workspace — the custom, SDK-driven UI that replaces the
 * CometChat demo home. Matches docs/dbm-chat-1-0.html.
 */
export default function DBMWorkspace() {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [clock, setClock] = useState(clockLabel());
  const [activeTab, setActiveTab] = useState("home");
  const [toast, setToast] = useState<string | null>(null);
  const [showSwitcher, setShowSwitcher] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const me = CometChatUIKitLoginListener.getLoggedInUser();
  const isEmbedded = window.parent !== window;
  const [poppingOut, setPoppingOut] = useState(false);

  // One-time name confirmation — covers new AND existing users alike,
  // since neither has ever had a chance to lock a name in before now.
  const [needsNameSetup, setNeedsNameSetup] = useState(() => !readNameLocked(me));

  // Admin-broadcast notifications (see broadcastNotify.ts) — separate
  // system from What's New: this is live/admin-triggered, not a code
  // release.
  const { history: notifHistory, toast: broadcastToast, unreadCount, openToast, markAllRead } =
    useBroadcastNotifications();
  const [adminInfo, setAdminInfo] = useState<{ isAdmin: boolean; email?: string; admins: AdminInfo[] }>({
    isAdmin: false,
    admins: [],
  });
  const [showAdmin, setShowAdmin] = useState(false);
  useEffect(() => {
    checkAdminStatus()
      .then(setAdminInfo)
      .catch(() => {});
  }, []);

  const [showRelease, setShowRelease] = useState(false);
  const [unseenRelease, markReleaseSeen] = useHasUnseenRelease();

  const [sidePanel, setSidePanel] = useState<"later" | "activity" | "calls" | "files" | null>(null);
  const [profileUid, setProfileUid] = useState<string | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  // Light by default on first visit — dark only once someone's explicitly
  // toggled to it (and it's been remembered since).
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light")
  );

  // Unread badge on the Activity rail tab — without it there's no cue that
  // anything landed there short of opening the panel.
  const [unseenActivity, setUnseenActivity] = useState(unseenActivityCount);
  useEffect(() => subscribeActivity(() => setUnseenActivity(unseenActivityCount())), []);
  useEffect(() => {
    if (sidePanel === "activity") markActivitySeen();
  }, [sidePanel]);

  // Theme lives on <html> so portaled overlays (calls, toasts) follow too.
  useEffect(() => {
    if (theme === "light") document.documentElement.setAttribute("data-dbm-theme", "light");
    else document.documentElement.removeAttribute("data-dbm-theme");
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // ⌘K / Ctrl+K opens the quick switcher from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowSwitcher((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Selecting anything closes the mobile drawer.
  const handleSelect = (s: Selection) => {
    // Pin a person into the sidebar the moment you open them, rather than
    // only once a message makes it a real conversation (see openedDms.ts).
    // Every route to a DM — search, directory, forward, profile — funnels
    // through here, so this is the one place it needs to happen.
    if (s.kind === "user") rememberOpenedDm(s.id, s.name);
    setSelection(s);
    setSideOpen(false);
  };

  useNotifications({ currentSelection: selection, onOpen: handleSelect });
  // Silently re-establishes the chat connection if this tab's token stops
  // working — otherwise the page looks fine while receiving nothing.
  useSessionRecovery();
  useLaterEngine(showToast);
  useActivityLogger(selection);
  useGroupSync({
    meUid: me?.getUid(),
    onRemoved: (guid, name) => {
      setSelection((prev) => (prev?.kind === "group" && prev.id === guid ? null : prev));
      showToast(`You were removed from #${name}`);
    },
  });

  const popOut = () => {
    if (!isEmbedded || poppingOut) return;
    setPoppingOut(true);
    // Ask the portal page (which alone can see window.logged_in_user) to
    // re-sign a fresh handoff and open it in a new tab — same query-param
    // login path the gate already supports.
    ALLOWED_PARENT_ORIGINS.forEach((origin) => {
      window.parent.postMessage({ type: "DBM_CHAT_POPOUT_REQUEST" }, origin);
    });
    // Safety net in case the portal page never responds (e.g. it's running
    // an older, not-yet-updated version of the code block) — don't leave
    // the button stuck in a loading state forever.
    setTimeout(() => setPoppingOut(false), 8000);
  };

  // Clear the loading state once the portal page confirms the new tab
  // actually opened (or failed) — without this, the button gives no
  // feedback while the sign-handoff round trip is in flight.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!ALLOWED_PARENT_ORIGINS.includes(e.origin)) return;
      if (e.data?.type === "DBM_CHAT_POPOUT_DONE") setPoppingOut(false);
      if (e.data?.type === "DBM_CHAT_POPOUT_ERROR") {
        setPoppingOut(false);
        showToast("Couldn't open full screen — try again");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No session? Back to the gate.
  useEffect(() => {
    if (!me) navigate("/login", { replace: true });
  }, [me, navigate]);

  // Tick the top-bar clock each minute.
  useEffect(() => {
    const t = setInterval(() => setClock(clockLabel()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className={`dbm-ws${sideOpen ? " side-open" : ""}`}>
      <CallProvider>
      <div className="topbar">
        <button
          className="hamb"
          aria-label="Toggle sidebar"
          onClick={() => {
            sounds.playOnce(sounds.SOUND.click);
            setSideOpen((v) => !v);
          }}
        >
          ☰
        </button>
        <div className="logo">
          db<span className="m">m</span><span className="dot">.</span>
          <span className="logo-chat">chat</span>
        </div>
        <button
          className="gsearch"
          onClick={() => {
            sounds.playOnce(sounds.SOUND.click);
            setShowSwitcher(true);
          }}
        >
          <span>⌕</span>
          <span>Search DBM Partner Portal: people, channels, files…</span>
          <span className="kbd">{IS_MAC ? "⌘ K" : "Ctrl K"}</span>
        </button>
        <button
          className={`theme-toggle release-btn ${unseenRelease ? "has-new" : ""}`}
          title="What's new"
          aria-label="What's new"
          onClick={() => {
            sounds.playOnce(sounds.SOUND.click);
            markReleaseSeen();
            setShowRelease(true);
          }}
        >
          <SparkleIcon size={17} />
          {unseenRelease && <span className="release-btn__dot" />}
        </button>
        <NotificationBell history={notifHistory} unreadCount={unreadCount} onMarkAllRead={markAllRead} />
        {adminInfo.isAdmin && (
          <button
            className="theme-toggle"
            title="Admin"
            aria-label="Admin"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              setShowAdmin(true);
            }}
          >
            <ShieldIcon size={17} />
          </button>
        )}
        <button
          className="theme-toggle"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label="Toggle light/dark mode"
          onClick={() => {
            sounds.playOnce(sounds.SOUND.click);
            setTheme((t) => (t === "dark" ? "light" : "dark"));
          }}
        >
          {theme === "dark" ? <SunIcon size={17} /> : <MoonIcon size={16} />}
        </button>
        {isEmbedded && (
          <button
            className="hbtn popout-btn"
            title="Open in full screen"
            aria-label="Open in full screen"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              popOut();
            }}
            disabled={poppingOut}
          >
            {poppingOut ? <span className="spin" /> : <span className="popout-icon">⤢</span>}
          </button>
        )}
        <span className="clock">{clock}</span>
      </div>

      <div className="workrow">
        <nav className="apprail" aria-label="App navigation">
          <div className="mtile">m.</div>
          {RAIL_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`railbtn ${activeTab === tab.key ? "on" : ""}`}
              onClick={() => {
                sounds.playOnce(sounds.SOUND.click);
                if (tab.key === "home") {
                  setActiveTab("home");
                  setSidePanel(null);
                } else {
                  setActiveTab(tab.key);
                  setSidePanel((v) => (v === tab.key ? null : (tab.key as typeof sidePanel)));
                }
              }}
            >
              <i>{tab.icon}</i>
              {tab.key === "activity" && unseenActivity > 0 && (
                <span className="bdg">{unseenActivity > 9 ? "9+" : unseenActivity}</span>
              )}
              <span>{tab.label}</span>
            </button>
          ))}
          <div className="railme">
            {me?.getUid() && (
              <AvatarHoverCard uid={me.getUid()} name={me.getName() || "You"} onOpenProfile={setProfileUid}>
                <Avatar
                  uid={me.getUid()}
                  name={me.getName()}
                  className="railme-btn"
                  colored={false}
                  style={{ background: "var(--orange)", color: "var(--ink)" }}
                >
                  <span className="st onl" />
                </Avatar>
              </AvatarHoverCard>
            )}
          </div>
        </nav>

        {/* Tap-outside-to-close for the mobile drawer. Only rendered while
            it's open, and hidden by CSS above the drawer breakpoint, so a
            desktop layout (where the sidebar is a permanent column, not an
            overlay) never gets an invisible click-catcher over it. */}
        {sideOpen && (
          <button
            className="side-scrim"
            aria-label="Close menu"
            onClick={() => setSideOpen(false)}
          />
        )}
        <Sidebar selection={selection} onSelect={handleSelect} onOpenProfile={setProfileUid} />
        <MessagePane selection={selection} onCleared={() => setSelection(null)} onOpenProfile={setProfileUid} />
      </div>

      {showSwitcher && (
        <QuickSwitcher onClose={() => setShowSwitcher(false)} onSelect={handleSelect} />
      )}

      {sidePanel === "later" && <LaterPanel onClose={() => setSidePanel(null)} onOpen={handleSelect} />}
      {sidePanel === "activity" && <ActivityPanel onClose={() => setSidePanel(null)} onOpen={handleSelect} />}
      {sidePanel === "calls" && <CallsPanel onClose={() => setSidePanel(null)} onOpen={handleSelect} />}
      {sidePanel === "files" && <FilesPanel onClose={() => setSidePanel(null)} onOpen={handleSelect} />}

      {profileUid && (
        <ProfilePanel
          uid={profileUid}
          meUid={me?.getUid()}
          onClose={() => setProfileUid(null)}
          onMessage={(uid, name) => {
            handleSelect({ kind: "user", id: uid, name, subtitle: "Direct message" });
            setProfileUid(null);
          }}
        />
      )}

      {showRelease && <ReleaseNotes onClose={() => setShowRelease(false)} />}

      {needsNameSetup && me && (
        <NameSetupModal
          initialFirst={splitName(me.getName()).first}
          initialLast={splitName(me.getName()).last}
          onDone={() => setNeedsNameSetup(false)}
        />
      )}

      {showAdmin && (
        <AdminPanel admins={adminInfo.admins} myEmail={adminInfo.email} onClose={() => setShowAdmin(false)} />
      )}

      {broadcastToast && (
        <BroadcastToast notification={broadcastToast} onOpen={() => openToast(broadcastToast)} />
      )}

      {toast && <div className="dbm-toast">{toast}</div>}
      </CallProvider>
    </div>
  );
}
